/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode } from "react"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"

/**
 * "Medaica Report — Quick Start" — the prototype's Help modal
 * (cardiac_report_v0_6_1.html #helpModal), wording, emphasis, tables and
 * sizes as it has them. The one difference is the footer, which drops the
 * prototype's app version (the portal shows the version in the About dialog
 * only).
 */

const TEXT_SX = { fontSize: 13, lineHeight: 1.6, color: "#1a1a1a" }

const SectionTitle = ({ children }: { children: string }): ReactElement => (
  <Typography
    component="h4"
    sx={{
      m: "16px 0 6px",
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      color: "#555",
    }}
  >
    {children}
  </Typography>
)

const Para = ({ children }: { children: ReactNode }): ReactElement => (
  <Typography component="p" sx={{ ...TEXT_SX, m: "0 0 8px" }}>
    {children}
  </Typography>
)

/** The prototype's two-column guide tables: a term and its meaning, a rule between rows. */
const GuideRows = ({ rows }: { rows: { key: string; label: ReactNode; text: string }[] }): ReactElement => (
  <Box
    component="table"
    sx={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12,
      lineHeight: 1.6,
      color: "#1a1a1a",
      mb: "4px",
      "& tr:not(:last-child)": { borderBottom: "1px solid #e0e0e0" },
      "& td": { p: "5px 0", verticalAlign: "top", border: 0 },
      "& td:first-of-type": { pr: 1, fontWeight: 600 },
    }}
  >
    <tbody>
      {rows.map((row) => (
        <tr key={row.key}>
          <td>{row.label}</td>
          <td>{row.text}</td>
        </tr>
      ))}
    </tbody>
  </Box>
)

const STATUS_ROWS: { label: string; color: string; meaning: string }[] = [
  { label: "Normal", color: "#2e7d32", meaning: "Within expected range" },
  { label: "Borderline", color: "#e65100", meaning: "Near the boundary — monitor or follow up" },
  { label: "Elevated", color: "#c62828", meaning: "Above expected range" },
  { label: "Low", color: "#c62828", meaning: "Below expected range" },
]

const WORKFLOW_STEPS: { key: string; text: ReactNode }[] = [
  {
    key: "unlock",
    text: (
      <>
        Click <strong>Edit</strong> and enter the password to unlock report editing controls.
      </>
    ),
  },
  { key: "review", text: "Review each final report card — value, status, and patient/provider wording." },
  { key: "values", text: "Edit values by dragging the marker or typing directly. Status updates automatically." },
  {
    key: "validation",
    text: "Recording and evidence validation is completed before export in Medaica Viewer.",
  },
  {
    key: "rewind",
    text:
      "Edit a marker or note as needed. A rewind icon appears only on changed cards and restores the exported " +
      "result.",
  },
  {
    key: "approve",
    text: (
      <>
        Use the single global <strong>Approve &amp; Exit</strong> action when the report is ready. There is no per-card
        approval step.
      </>
    ),
  },
  { key: "blocked", text: "Any unresolved edit confirmation blocks approval and identifies the affected card." },
]

const ROLLUP_ROWS: { features: string; rule: string }[] = [
  {
    features: "Heart Rate, SYS:DIA Ratio, SYS:DIA Duration",
    rule: "Robust average/median across accepted recordings; preserve range in Review",
  },
  { features: "Signal Quality", rule: "Per-source gate; poor sources are excluded or marked limited" },
  { features: "Cycle Regularity", rule: "Flags if any accepted source has credible irregular timing" },
  { features: "Structural Sound", rule: "Reported peak findings from accepted recordings are surfaced" },
]

const EDIT_BUTTON_HELP = (
  <>
    When unlocked, the button reads <strong>Exit</strong> until a change is made, then{" "}
    <strong>Approve &amp; Exit</strong>.
  </>
)

const HeartReportHelpDialog = ({ open, onClose }: { open: boolean; onClose: () => void }): ReactElement => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pb: 1,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      Medaica Report — Quick Start
      <IconButton aria-label="Close help" size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <SectionTitle>Report Views</SectionTitle>
      <Para>
        <strong>Patient Report</strong> — colour-coded cards with plain-language notes for the patient. No clinical
        terminology.
      </Para>
      <Para>
        <strong>Provider Report</strong> — prose clinical document formatted for a referring physician. No colour
        gradients; status shown as Normal / Borderline / Elevated / Low.
      </Para>
      <Para>
        <strong>Edit</strong> — password-protected report editing. {EDIT_BUTTON_HELP}
      </Para>

      <SectionTitle>Status Labels</SectionTitle>
      <GuideRows
        rows={STATUS_ROWS.map((row) => ({
          key: row.label,
          label: <span style={{ color: row.color }}>{row.label}</span>,
          text: row.meaning,
        }))}
      />

      <SectionTitle>Review Workflow</SectionTitle>
      <Box
        component="ol"
        sx={{ ...TEXT_SX, m: "0 0 8px", pl: "18px", listStyle: "decimal", "& li": { display: "list-item", mb: "5px" } }}
      >
        {WORKFLOW_STEPS.map((step) => (
          <li key={step.key}>{step.text}</li>
        ))}
      </Box>

      <SectionTitle>Notes</SectionTitle>
      <Para>
        Notes are auto-populated on first load — <strong>patient notes</strong> in plain language;{" "}
        <strong>provider notes</strong> in clinical language with guideline references where applicable.
      </Para>
      <Para>
        If you edit a note, it becomes the confirmed text. If the feature status later changes, a{" "}
        <em>&quot;Confirm note is still current&quot;</em> prompt will appear. Dismiss it to acknowledge.
      </Para>
      <Para>The rewind icon restores the exported value and wording, then clears any staleness warning.</Para>

      <SectionTitle>Viewer Review</SectionTitle>
      <Para>
        Recording suitability, timing consistency, and supporting evidence are reviewed in Medaica Viewer. Report is the
        downstream patient-facing output and does not repeat those validation steps.
      </Para>

      <SectionTitle>Multiple Recordings</SectionTitle>
      <Typography component="p" sx={{ ...TEXT_SX, m: "0 0 6px" }}>
        When an exam includes recordings from more than one position, sources appear as compact playback chips in Exam
        Source. Only accepted/reportable recordings drive exam-level cards; excluded sources remain in the
        evidence/audit trail.
      </Typography>
      <GuideRows rows={ROLLUP_ROWS.map((row) => ({ key: row.features, label: row.features, text: row.rule }))} />

      <Box sx={{ mt: "20px", pt: "12px", borderTop: "1px solid #e0e0e0", fontSize: 11, color: "#888" }}>
        Medaica Report &nbsp;·&nbsp; Provisional results are review aids, not diagnostic. Not an ECG or echocardiogram.
      </Box>
    </DialogContent>
  </Dialog>
)

export default HeartReportHelpDialog
